import { NotificationLevel, NotificationStatusBar } from '@today/ui-components'
import { AdminShellView } from '@today/ui-domain'
import type { ReactNode } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useAdminSections } from '@/hooks/useAdminSections'
import { useOnboardingGate } from '@/hooks/useOnboardingGate'
import { ADMIN_PLUGIN_SECTIONS, type AdminPluginSection } from '@/modules/admin-sections/plugin-registry'
import type { AdminSectionLink } from '@/modules/admin-sections/visible-sections'
import { adminPluginHref, canOpenPluginSection } from '@/modules/admin-sections/visible-sections'
import { ForbiddenPage } from '@/pages/errors/Forbidden'
import { NotFoundPage } from '@/pages/NotFound'

interface AdminShellPageProps {
	/** Plugin admin sections; the shipped registry by default (tests inject their own). */
	plugins?: readonly AdminPluginSection[]
}

function Shell({
	sections,
	currentHref,
	children,
}: {
	sections: AdminSectionLink[]
	currentHref?: string
	children: ReactNode
}) {
	const navigate = useNavigate()
	return (
		<AdminShellView
			sections={sections.map((section) => ({ ...section, current: section.href === currentHref }))}
			onNavigate={(href) => void navigate(href)}
		>
			{children}
		</AdminShellView>
	)
}

/**
 * `/admin` — the admin shell (TO-189): the sections the viewer may open, or the
 * Forbidden page when there are none. A navigation aid only: every destination
 * and its API enforce their own permission.
 */
export function AdminShellPage({ plugins = ADMIN_PLUGIN_SECTIONS }: AdminShellPageProps) {
	const state = useAdminSections(plugins)
	const onboarding = useOnboardingGate()
	if (state.isLoading || onboarding.isPending) return null
	// TO-190 REQ-008: a Tenant Admin whose tenant has not finished onboarding goes to the wizard.
	if (onboarding.redirectTo) return <Navigate to={onboarding.redirectTo} replace />
	if (state.sections.length === 0) return <ForbiddenPage />
	return (
		<Shell sections={state.sections}>
			{onboarding.resumeHref && (
				<ResumeOnboardingBanner href={onboarding.resumeHref} onDismiss={onboarding.dismissResume} />
			)}
			<h1 className="text-2xl font-bold text-ds-text">Admin</h1>
			<p className="text-sm text-ds-text-muted mt-1">Choose a section.</p>
		</Shell>
	)
}

/** TO-190 REQ-011: optional onboarding steps remain — offer to resume, dismissable for the session. */
function ResumeOnboardingBanner({ href, onDismiss }: { href: string; onDismiss: () => void }) {
	return (
		<div className="mb-4 flex flex-col gap-2">
			<NotificationStatusBar
				onDismiss={onDismiss}
				items={[
					{
						id: 'resume-onboarding',
						level: NotificationLevel.INFO,
						title: 'Some optional setup steps are still open',
					},
				]}
			/>
			<Link to={href} className="text-ds-sm text-ds-primary underline">
				Resume onboarding
			</Link>
		</div>
	)
}

/**
 * `/admin/plugins/:code` — a plugin admin section inside the shell (TO-189 REQ-006).
 * Another tenant's plugin renders exactly like an unknown code (Not Found), so a
 * viewer never learns which plugins other tenants have.
 */
export function AdminPluginSectionPage({ plugins = ADMIN_PLUGIN_SECTIONS }: AdminShellPageProps) {
	const { code = '' } = useParams()
	const state = useAdminSections(plugins)
	if (state.isLoading) return null

	const section = state.viewer.slug === null ? undefined : plugins.find((entry) => entry.code === code)
	if (!section || section.tenantSlug !== state.viewer.slug) return <NotFoundPage />
	if (!canOpenPluginSection(section, state.viewer)) return <ForbiddenPage />

	const { component: Plugin } = section
	return (
		<Shell sections={state.sections} currentHref={adminPluginHref(code)}>
			<Plugin tenantSlug={section.tenantSlug} code={code} />
		</Shell>
	)
}
